/** The AI assistant's settings - ADR-0192, read and written as the workspace Admin.
 *
 * **Everything here is Admin-only on the server** (`require_roles(Role.ADMIN)`), so every hook takes
 * an `enabled` flag and a non-Admin's screen never asks: a request made only to be refused shows
 * as a failed call in every network log. The spend read is the one exception - it is open to every
 * role that works in the workspace (an Admin always sees it, whatever state the assistant is in) -
 * but this module serves the Admin's screen only.
 *
 * **The credential is write-only.** No response carries it, a prefix, a suffix or a hash; the
 * server says *set* or *not set* and the date. The mutation that writes one has `gcTime: 0`, so the
 * key is not kept in the mutation cache after the form is left, and nothing in this file puts it
 * in a query key, a log or storage.
 *
 * A read that fails throws (never a default): a settings screen that showed "AI is off" because the
 * read failed would be answering a question it had not been told.
 */

import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";

import { api } from "./client";
import { refusalFrom } from "./refusal";
import type { components } from "./schema";

export type AssistantConfig = components["schemas"]["AssistantConfigOut"];
export type AssistantConfigInput = components["schemas"]["AssistantConfigIn"];
export type AssistantSpend = components["schemas"]["AssistantSpendOut"];
export type AssistantStatus = components["schemas"]["AssistantStatusOut"];
export type AssistantAnswer = components["schemas"]["AssistantAskOut"];

export const assistantKeys = {
  config: () => ["assistant", "config"] as const,
  spend: () => ["assistant", "spend"] as const,
  status: () => ["assistant", "status"] as const,
};

export function useAssistantConfig(enabled: boolean) {
  return useQuery({
    queryKey: assistantKeys.config(),
    enabled,
    retry: false,
    queryFn: async (): Promise<AssistantConfig> => {
      const { data, error, response } = await api.GET("/api/v1/assistant/config", {});
      if (!data) throw refusalFrom(response, error, "The AI assistant's settings could not be read.");
      return data;
    },
  });
}

/** Is the assistant on for the person asking, and if not, the first condition that fails - in the
 * server's own words. Open to every role (an Auditor is told too), so Ask can read it without being an
 * Admin's screen. A read that fails throws, never a default: Ask must not say "off because X" from a
 * request that did not answer. */
export function useAssistantStatus() {
  return useQuery({
    queryKey: assistantKeys.status(),
    retry: false,
    queryFn: async (): Promise<AssistantStatus> => {
      const { data, error, response } = await api.GET("/api/v1/assistant/status", {});
      if (!data) throw refusalFrom(response, error, "Whether the AI assistant is on could not be read.");
      return data;
    },
  });
}

export function useAssistantSpend(enabled: boolean) {
  return useQuery({
    queryKey: assistantKeys.spend(),
    enabled,
    retry: false,
    queryFn: async (): Promise<AssistantSpend> => {
      const { data, error, response } = await api.GET("/api/v1/assistant/spend", {});
      if (!data) throw refusalFrom(response, error, "This month's spend could not be read.");
      return data;
    },
  });
}

/** Saves the provider configuration as a new version (a no-op if it matches the current one). */
export function useSaveAssistantConfig() {
  const queryClient = useQueryClient();
  return useMutation({
    retry: false,
    mutationFn: async (body: AssistantConfigInput): Promise<AssistantConfig> => {
      const { data, error, response } = await api.PUT("/api/v1/assistant/config", { body });
      if (!data) throw refusalFrom(response, error, "The AI assistant's settings could not be saved.");
      return data;
    },
    onSuccess: async (saved) => {
      queryClient.setQueryData(assistantKeys.config(), saved);
      await queryClient.invalidateQueries({ queryKey: assistantKeys.spend() });
    },
  });
}

/** Keeps the provider key. Write-only; `gcTime: 0` so the variables are not kept afterwards. */
export function useSetAssistantCredential() {
  const queryClient = useQueryClient();
  return useMutation({
    retry: false,
    gcTime: 0,
    mutationFn: async (credential: string): Promise<void> => {
      const { error, response } = await api.PUT("/api/v1/assistant/credential", {
        body: { credential },
      });
      if (!response.ok) throw refusalFrom(response, error, "The key could not be saved.");
    },
    onSuccess: async () => {
      await queryClient.invalidateQueries({ queryKey: assistantKeys.config() });
    },
  });
}

/** Disconnects: the key is removed and calls stop. The record of what was spent is kept. */
export function useRemoveAssistantCredential() {
  const queryClient = useQueryClient();
  return useMutation({
    retry: false,
    mutationFn: async (): Promise<void> => {
      const { error, response } = await api.DELETE("/api/v1/assistant/credential", {});
      if (!response.ok) throw refusalFrom(response, error, "The key could not be removed.");
    },
    onSuccess: async () => {
      await queryClient.invalidateQueries({ queryKey: assistantKeys.config() });
    },
  });
}

/** A fixed one-word request that sends no customer data. Counts as a call, and is recorded. */
export function useAssistantConnectionTest() {
  const queryClient = useQueryClient();
  return useMutation({
    retry: false,
    mutationFn: async (): Promise<AssistantAnswer> => {
      const { data, error, response } = await api.POST("/api/v1/assistant/connection-test", {});
      if (!data) throw refusalFrom(response, error, "The connection could not be tested.");
      return data;
    },
    onSuccess: async () => {
      await queryClient.invalidateQueries({ queryKey: assistantKeys.spend() });
    },
  });
}

/** Where this month's usage rows download as CSV (every cell defused for a spreadsheet). */
export const ASSISTANT_SPEND_EXPORT = "/api/v1/assistant/spend/export";
